import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Calendar, Check, X, Sparkles, Phone, Mail, Video } from "lucide-react";

const AVAILABLE_HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17]; // 9am-5pm local

/**
 * Continue-to-Consultation flow: shows selected services, gathers project
 * intake, then a date + time picker in the client's local timezone. On submit
 * → POST /api/client/service-requests, then routes to /portal/my-service-requests.
 */
export default function ClientServiceCheckout() {
  const nav = useNavigate();
  const [ids, setIds] = useState([]);
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [intake, setIntake] = useState({
    primary_goal: "",
    business_status: "",
    desired_launch_date: "",
    additional_notes: "",
    best_phone: "",
    preferred_contact: "phone",
    assigned_user_id: "",
  });

  const [pickedDate, setPickedDate] = useState("");
  const [pickedHour, setPickedHour] = useState(null);
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      let selectedIds = [];
      try { selectedIds = JSON.parse(sessionStorage.getItem("cq_services_selected") || "[]"); } catch { selectedIds = []; }
      if (!selectedIds?.length) {
        toast.error("Pick at least one service first");
        nav("/portal/services");
        return;
      }
      setIds(selectedIds);
      try {
        const [meResp, allResp] = await Promise.all([
          api.get("/client/me"),
          api.get("/services?limit=500"),
        ]);
        if (cancel) return;
        setMe(meResp.data);
        setTeam((meResp.data?.team || []).filter((t) => t.avatar_url));
        const byId = Object.fromEntries((allResp.data?.items || []).map((s) => [s.id, s]));
        setServices(selectedIds.map((id) => byId[id]).filter(Boolean));
        setIntake((prev) => ({ ...prev, best_phone: prev.best_phone || meResp.data?.phone || "" }));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      }
    })();
    return () => { cancel = true; };
  }, [nav]);

  const removeSvc = (id) => {
    const next = ids.filter((x) => x !== id);
    setIds(next);
    setServices((prev) => prev.filter((s) => s.id !== id));
    try { sessionStorage.setItem("cq_services_selected", JSON.stringify(next)); } catch { /* no-op */ }
  };

  const buildSlotIso = () => {
    if (!pickedDate || pickedHour == null) return null;
    const dt = new Date(`${pickedDate}T${String(pickedHour).padStart(2, "0")}:00:00`);
    return dt.toISOString();
  };
  const buildSlotLocal = () => {
    if (!pickedDate || pickedHour == null) return "";
    const dt = new Date(`${pickedDate}T${String(pickedHour).padStart(2, "0")}:00:00`);
    return dt.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const submit = async () => {
    const slot_iso = buildSlotIso();
    if (!slot_iso) {
      toast.error("Pick a date and time for your consultation");
      return;
    }
    if (!intake.best_phone) {
      toast.error("Add a phone number so your team can confirm");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/client/service-requests", {
        service_ids: ids,
        primary_goal: intake.primary_goal,
        business_status: intake.business_status,
        desired_launch_date: intake.desired_launch_date,
        additional_notes: intake.additional_notes,
        best_phone: intake.best_phone,
        preferred_contact: intake.preferred_contact,
        preferred_slot_iso: slot_iso,
        preferred_slot_local: buildSlotLocal(),
        timezone: tz,
        assigned_user_id: intake.assigned_user_id || undefined,
      });
      try { sessionStorage.removeItem("cq_services_selected"); } catch { /* no-op */ }
      setConfirmed(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit — try again");
    } finally { setBusy(false); }
  };

  // Next 14 days for the date picker
  const dateOptions = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 1; i <= 14; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      days.push({ iso, label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) });
    }
    return days;
  }, []);

  const addToCalendarUrl = () => {
    if (!confirmed?.preferred_slot_iso) return "#";
    const start = new Date(confirmed.preferred_slot_iso);
    const end = new Date(start.getTime() + 45 * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
    const title = encodeURIComponent(`Credit Quack — New Services Consultation`);
    const details = encodeURIComponent(`Selected services:\n${(confirmed.services || []).map((s) => `• ${s.name}`).join("\n")}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
  };

  if (confirmed) {
    return (
      <AppShell>
        <div className="cq-in max-w-2xl" data-testid="services-confirmation">
          <Card className="p-8 rounded-3xl border-emerald-200 shadow-lg bg-gradient-to-br from-white to-emerald-50">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center">
                <Check size={26} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-emerald-700">Booked</p>
                <h1 className="font-display text-3xl sm:text-4xl font-black text-slate-900 mt-1">
                  Your New Services Consultation Is Confirmed
                </h1>
              </div>
            </div>

            <div className="mt-6 space-y-2 text-sm text-slate-700">
              <p><b>Selected services:</b> {(confirmed.services || []).map((s) => s.name).join(", ")}</p>
              <p><b>Assigned team member:</b> {confirmed.assigned_user_name || "Your Credit Quack team"}</p>
              <p><b>Date & time:</b> {confirmed.preferred_slot_local} <span className="text-slate-500">({confirmed.timezone})</span></p>
              <p><b>Meeting method:</b> {confirmed.preferred_contact === "phone" ? "Phone call" : confirmed.preferred_contact === "text" ? "Text conversation" : confirmed.preferred_contact === "email" ? "Email" : "Zoom / video"}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <a
                href={addToCalendarUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-bold hover:bg-slate-800"
                data-testid="services-add-to-calendar"
              >
                <Calendar size={14} /> Add to calendar
              </a>
              <Button
                variant="outline"
                onClick={() => nav("/portal/my-service-requests")}
                data-testid="services-view-requests"
              >
                View my requests
              </Button>
              <Button
                variant="outline"
                onClick={() => nav("/portal/services")}
              >
                Browse more services
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-5">
              We'll send an SMS confirmation and reminder. Need to change it? Head to <b>My Service Requests</b> to reschedule or cancel.
            </p>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="cq-in max-w-4xl">
        <button
          onClick={() => nav("/portal/services")}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-800 mb-2"
          data-testid="checkout-back"
        >
          <ArrowLeft size={14} /> Back to services
        </button>
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Client portal · Consultation</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Continue to Consultation</h1>
        <p className="text-slate-600 mt-2">Confirm your selections, tell us the goal, then pick a time that works for you.</p>

        {/* Summary */}
        <Card className="mt-6 p-5 rounded-2xl border-slate-100" data-testid="checkout-summary">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Your selected services</p>
          <div className="flex flex-wrap gap-2">
            {services.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 text-xs font-bold">
                {s.name}
                <button onClick={() => removeSvc(s.id)} className="ml-1 text-amber-600 hover:text-amber-900" aria-label={`Remove ${s.name}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </Card>

        {/* Intake */}
        <Card className="mt-4 p-5 rounded-2xl border-slate-100 space-y-4" data-testid="checkout-intake">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500">A few quick questions</p>
          <FieldRow label="Primary goal">
            <Input value={intake.primary_goal} onChange={(e) => setIntake((p) => ({ ...p, primary_goal: e.target.value }))} placeholder="e.g. Launch my LLC and secure $50K in business credit within 60 days" data-testid="intake-goal" />
          </FieldRow>
          <FieldRow label="Current business or project status">
            <Input value={intake.business_status} onChange={(e) => setIntake((p) => ({ ...p, business_status: e.target.value }))} placeholder="e.g. Idea stage / Registered but no revenue / Making $10K/mo" data-testid="intake-status" />
          </FieldRow>
          <FieldRow label="Desired launch date">
            <Input type="date" value={intake.desired_launch_date} onChange={(e) => setIntake((p) => ({ ...p, desired_launch_date: e.target.value }))} data-testid="intake-launch" />
          </FieldRow>
          <FieldRow label="Additional notes">
            <Textarea value={intake.additional_notes} onChange={(e) => setIntake((p) => ({ ...p, additional_notes: e.target.value }))} placeholder="Anything else your team should know before the call" rows={3} data-testid="intake-notes" />
          </FieldRow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldRow label="Best phone number">
              <Input value={intake.best_phone} onChange={(e) => setIntake((p) => ({ ...p, best_phone: e.target.value }))} placeholder="+1…" data-testid="intake-phone" />
            </FieldRow>
            <FieldRow label="Preferred contact method">
              <div className="flex flex-wrap gap-2">
                {[["phone", <Phone size={12} />, "Phone"],["text", <Sparkles size={12} />, "Text"],["email", <Mail size={12} />, "Email"],["zoom", <Video size={12} />, "Zoom"]].map(([v, ic, l]) => (
                  <button
                    key={v}
                    onClick={() => setIntake((p) => ({ ...p, preferred_contact: v }))}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold border ${intake.preferred_contact === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
                    data-testid={`intake-contact-${v}`}
                  >
                    {ic} {l}
                  </button>
                ))}
              </div>
            </FieldRow>
          </div>
        </Card>

        {/* Scheduling */}
        <Card className="mt-4 p-5 rounded-2xl border-slate-100" data-testid="checkout-scheduler">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-amber-500" />
            <h3 className="font-display text-lg font-black text-slate-900">Pick your consultation time</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Times are in <b>{tz}</b> · 45-minute virtual consultation.
          </p>

          <p className="text-xs font-bold text-slate-500 mb-2">Date</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {dateOptions.map((d) => (
              <button
                key={d.iso}
                onClick={() => setPickedDate(d.iso)}
                className={`px-3 py-2 rounded-xl border text-xs font-bold transition ${pickedDate === d.iso ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                data-testid={`date-${d.iso}`}
              >
                {d.label}
              </button>
            ))}
          </div>

          <p className="text-xs font-bold text-slate-500 mb-2">Time</p>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_HOURS.map((h) => {
              const label = new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
              return (
                <button
                  key={h}
                  onClick={() => setPickedHour(h)}
                  disabled={!pickedDate}
                  className={`px-3 py-2 rounded-xl border text-xs font-bold transition disabled:opacity-40 ${pickedHour === h ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                  data-testid={`time-${h}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {pickedDate && pickedHour != null && (
            <p className="mt-4 text-sm text-slate-600">
              <b>Selected:</b> {buildSlotLocal()} · {tz}
            </p>
          )}
        </Card>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={submit}
            disabled={busy || services.length === 0}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            data-testid="checkout-submit"
          >
            {busy ? "Confirming…" : "Confirm my consultation"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <p className="text-xs font-bold text-slate-700 mb-1.5">{label}</p>
      {children}
    </label>
  );
}
